import React from "react";
import { InfoIcon } from "../../../assets/Icons";

type StatusType =
  | "IN_TRANSIT"
  | "COMPLETED"
  | "CANCELLED"
  | "MAPPED"
  | "PAYMENT_NOT_FOUND"
  | "AWAITING_CONFIRMATION_FROM_CONVERA"
  | "DEAL_AWAITING_PAYMENT"
  | "DEAL_AWAITING_REVIEW"
  | "DEAL_POSTED"
  | "TRANSACTION_CCT_COMPLIANCE_CLEARED"
  | "DEAL_DISBURSED"
  | "TRANSACTION_CCT_COMPLIANCE_HOLD"
  | "TRANSACTION_CCT_COMPLIANCE_BLOCKED"
  | "TRANSACTION_CCT_COMPLIANCE_CANCELLED"
  | "DEAL_OVERDUE"
  | "DEAL_EXPIRED"
  | "REFUNDED"
  | "DEAL_RETURNED"
  | "DEAL_REJECTED"
  | "DEAL_VOIDED"
  | "REFUND_APPROVED"
  | "REFUND_REQUESTED";

type StatusProps = { status: any; selectedTab?: string };

const Status: React.FC<StatusProps> = ({ status, selectedTab }) => {
  const [showInfo, setShowInfo] = React.useState(false);
  const Color: Record<
    StatusType,
    {
      classColor: string;
      circleColor: string;
      text: string;
      textColor: string;
      title?: string;
      description?: string;
    }
  > = {
    IN_TRANSIT: {
      classColor: "bg-[#FFF3E9]",
      circleColor: "bg-[#DF6F07]",
      text: "In Progress",
      textColor: "text-[#DF6F07]",
    },
    COMPLETED: {
      classColor: "bg-[#E1FCEF]",
      circleColor: "bg-[#004004]",
      text: "Completed",
      textColor: "text-[#004004]",
    },
    CANCELLED: {
      classColor: "bg-[#FFF3F3]",
      circleColor: "bg-[#A90B0B]",
      text: "Cancelled",
      textColor: "text-[#A90B0B]",
    },
    MAPPED: {
      classColor: "bg-[#E9F5FF]",
      circleColor: "bg-[#0078D4]",
      text: "Mapped",
      textColor: "text-[#0078D4]",
    },
    PAYMENT_NOT_FOUND: {
      classColor: "bg-[#FFF8E1]",
      circleColor: "bg-[#FFA726]",
      text: "Payment Not Found",
      textColor: "text-[#FFA726]",
    },

    // Convera-specific statuses
    AWAITING_CONFIRMATION_FROM_CONVERA: {
      classColor: "bg-[#FFF8E1]",
      circleColor: "bg-[#FFA500]",
      text: "Awaiting Confirmation",
      textColor: "text-[#FFA500]",
      title: "Convera is expecting your payment",
      description:
        "Convera has not yet received your payment, but there is no need to worry. If you have completed your transaction, please allow three business days for it to reach us. If, after three business days, the status has not been updated, kindly reach out to our support team with proof of payment for assistance.",
    },
    DEAL_AWAITING_PAYMENT: {
      classColor: "bg-[#FFF3E0]",
      circleColor: "bg-[#FF9800]",
      text: "Awaiting Payment",
      textColor: "text-[#FF9800]",
      title: "Convera is expecting your payment",
      description:
        "Convera has not yet received your payment, but there is no need to worry. If you have completed your transaction, please allow three business days for it to reach us. If, after three business days, the status has not been updated, kindly reach out to our support team with proof of payment for assistance.",
    },
    DEAL_AWAITING_REVIEW: {
      classColor: "bg-[#FFF3E0]",
      circleColor: "bg-[#FF9800]",
      text: "Awaiting Review",
      textColor: "text-[#FF9800]",
      title: "Convera is expecting your payment",
      description:
        "Convera has not yet received your payment, but there is no need to worry. If you have completed your transaction, please allow three business days for it to reach us. If, after three business days, the status has not been updated, kindly reach out to our support team with proof of payment for assistance.",
    },
    DEAL_POSTED: {
      classColor: "bg-[#E3F2FD]",
      circleColor: "bg-[#1976D2]",
      text: "Posted",
      textColor: "text-[#1976D2]",
      title: "Convera has received your payment",
      description:
        "Great news! We have received your payment, and it is now being sent to Convera. If we require additional information, we will reach out to you directly. Please allow two business days for this process.",
    },
    TRANSACTION_CCT_COMPLIANCE_CLEARED: {
      classColor: "bg-[#E8F5E9]",
      circleColor: "bg-[#388E3C]",
      text: "Compliance Cleared",
      textColor: "text-[#388E3C]",
      title: "Your payment has been sent to Convera",
      description:
        "Your payment is on its way to Convera! Please allow up to three business days for your payment to be reflected in your account with your institution.",
    },
    DEAL_DISBURSED: {
      classColor: "bg-[#E8F5E9]",
      circleColor: "bg-[#388E3C]",
      text: "Cleared",
      textColor: "text-[#388E3C]",
      title: "Your payment has been sent to Convera",
      description:
        "Your payment is on its way to Convera! Please allow up to three business days for your payment to be reflected in your account with your institution.",
    },
    TRANSACTION_CCT_COMPLIANCE_HOLD: {
      classColor: "bg-[#FFF8E1]",
      circleColor: "bg-[#FFC107]",
      text: "Compliance Hold",
      textColor: "text-[#FFC107]",
      title: "Convera has your payment and we are reviewing it",
      description:
        "Good news! We have received your payment, and it is now under review by our compliance team. This usually takes up to two business days.",
    },
    TRANSACTION_CCT_COMPLIANCE_BLOCKED: {
      classColor: "bg-[#FDEDEC]",
      circleColor: "bg-[#D9534F]",
      text: "Compliance Block",
      textColor: "text-[#D9534F]",
      title: "Convera has declined your payment",
      description:
        "Due to sanction restrictions, Convera is unable to process your payment. Please check your email for further details and next steps.",
    },
    TRANSACTION_CCT_COMPLIANCE_CANCELLED: {
      classColor: "bg-[#FDEDEC]",
      circleColor: "bg-[#D9534F]",
      text: "Compliance Cancelled",
      textColor: "text-[#D9534F]",
      title: "Convera has declined your payment",
      description:
        "We were unable to verify your identity. Your payment will be refunded to the original source. Please allow up to 10 business days.",
    },
    DEAL_OVERDUE: {
      classColor: "bg-[#FFEBEE]",
      circleColor: "bg-[#E53935]",
      text: "Overdue",
      textColor: "text-[#E53935]",
      title: "Payment not received by Convera",
      description:
        "Convera has not received your payment. If already made, please wait 3 business days. If still not updated, contact support with proof of payment.",
    },
    DEAL_EXPIRED: {
      classColor: "bg-[#F5F5F5]",
      circleColor: "bg-[#9E9E9E]",
      text: "Expired",
      textColor: "text-[#9E9E9E]",
      title: "Convera has not received your payment",
      description:
        "Convera has not received your payment. If paid, check if funds were deducted and contact support. Otherwise, please requote and pay again.",
    },
    REFUNDED: {
      classColor: "bg-[#E8F5E9]",
      circleColor: "bg-[#4CAF50]",
      text: "Refunded",
      textColor: "text-[#4CAF50]",
      title: "Your Payment has been refunded",
      description:
        "Your payment to Convera has been successfully refunded. Please allow up to 15 business days for it to reflect in your account.",
    },
    DEAL_RETURNED: {
      classColor: "bg-[#FFF3E0]",
      circleColor: "bg-[#FB8C00]",
      text: "Returned",
      textColor: "text-[#FB8C00]",
      title: "Payment returned to source",
      description:
        "The payment has been returned to your account due to either cancellation or failed processing. Please check with your bank for confirmation.",
    },
    DEAL_REJECTED: {
      classColor: "bg-[#FCE4EC]",
      circleColor: "bg-[#D81B60]",
      text: "Rejected",
      textColor: "text-[#D81B60]",
      title: "Payment rejected",
      description:
        "Your payment has been rejected by Convera. This may be due to compliance issues or incorrect details. Please check your email for more information.",
    },
    DEAL_VOIDED: {
      classColor: "bg-[#ECEFF1]",
      circleColor: "bg-[#607D8B]",
      text: "Voided",
      textColor: "text-[#607D8B]",
      title: "Transaction voided",
      description:
        "This transaction has been voided and will not be processed. You may initiate a new transaction if needed.",
    },
    REFUND_APPROVED: {
      classColor: "bg-[#E8F5E9]",
      circleColor: "bg-[#2E7D32]",
      text: "Refund Approved",
      textColor: "text-[#2E7D32]",
      title: "Refund approved",
      description:
        "Your refund has been approved and is being processed. Please allow several business days for it to reflect in your original payment method.",
    },
    REFUND_REQUESTED: {
      classColor: "bg-[#E0F2F1]",
      circleColor: "bg-[#009688]",
      text: "Refund Requested",
      textColor: "text-[#009688]",
      title: "Refund requested",
      description:
        "We have received your refund request. Our support team will review it and process the refund if eligible.",
    },
  };

  let parsedStatus: StatusType | string = status;
  let parsedReportTitle: string | undefined = undefined;
  let parsedReportDescription: string | undefined = undefined;

  if (typeof status === "string" && selectedTab === "Convera" && status.includes("|")) {
    const [statusPart, titlePart, descPart] = status.split(" | ");
    parsedStatus = statusPart?.trim() as StatusType;
    parsedReportTitle = titlePart?.trim();
    parsedReportDescription = descPart?.trim();
  }

  const currentStatus = Color[parsedStatus as StatusType] || {
    classColor: "bg-[#E0E0E0]",
    circleColor: "bg-[#9E9E9E]",
    text: "Unknown",
    textColor: "text-[#9E9E9E]",
  };

  const hexColor =
    currentStatus?.circleColor?.match(/#(?:[0-9a-fA-F]{3}){1,2}/)?.[0] || "#000000";

  return (
    <div className="flex items-center justify-start relative">
      <div className={`flex items-center justify-center w-fit px-2 py-1 rounded-full ${currentStatus.classColor}`}>
        <div className={`w-2 h-2 rounded-full ${currentStatus.circleColor}`} />
        <p className={`px-1 text-xs ${currentStatus.textColor}`}>{currentStatus.text}</p>
      </div>
      <div onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
        {selectedTab === "Convera" && <InfoIcon fillColor={hexColor} />}
        {showInfo && (
          <div
            className={`absolute z-40 ${currentStatus?.classColor} ${currentStatus?.textColor} text-xs p-3 rounded shadow-lg w-64 -left-28 top-full`}
          >
            {parsedReportTitle && (
              <p className="pb-2 font-bold text-black">{parsedReportTitle}</p>
            )}
            {parsedReportDescription && (
              <p className="text-gray-800 whitespace-pre-line">
                {parsedReportDescription.includes("$support") ? (
                  <>
                    {parsedReportDescription.split("$support").map((part, index, arr) => (
                      <React.Fragment key={index}>
                        {part}
                        {index !== arr.length - 1 && (
                          <a
                            href="https://students.convera.com/#!/contact-us"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            support
                          </a>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  parsedReportDescription
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Status;
